// <s-infinite-scroll> appends the next page of product cards when the shopper nears the end
// of the grid. Liquid writes the next page URL into data-next-url (from paginate.next.url);
// the server-rendered pagination stays as the no-JavaScript fallback and is hidden once this
// element upgrades. Filter/sort re-renders replace the element, which restarts from page 1.
class InfiniteScroll extends HTMLElement {
  #observer = null;
  #controller = null;
  #loading = false;

  connectedCallback() {
    const sentinel = this.querySelector('[data-infinite-sentinel]');
    if (!this.dataset.nextUrl || !sentinel) return;

    this.querySelector('[data-infinite-fallback]')?.setAttribute('hidden', '');
    this.#observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) this.#loadNext();
      },
      { rootMargin: '0px 0px 800px 0px' },
    );
    this.#observer.observe(sentinel);
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#controller?.abort();
  }

  async #loadNext() {
    const nextUrl = this.dataset.nextUrl;
    if (this.#loading || !nextUrl) return;
    this.#loading = true;
    this.#controller = new AbortController();
    const status = this.querySelector('[data-infinite-status]');
    if (status) status.hidden = false;

    try {
      const response = await fetch(nextUrl, { signal: this.#controller.signal });
      if (!response.ok) throw new Error(`Failed to load next page: ${response.status}`);
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const incoming = html.querySelector(`s-infinite-scroll#${CSS.escape(this.id)}`);
      const incomingItems = incoming?.querySelectorAll('[data-infinite-grid] > li') || [];
      const grid = this.querySelector('[data-infinite-grid]');

      for (const item of incomingItems) grid?.append(document.adoptNode(item));

      const followingUrl = incoming?.dataset.nextUrl || '';
      this.dataset.nextUrl = followingUrl;
      if (!followingUrl || !incomingItems.length) this.#finish();
    } catch (error) {
      if (error.name === 'AbortError') return;
      console.error('[s-infinite-scroll]', error);
      // Fall back to page links if loading fails.
      this.#observer?.disconnect();
      this.querySelector('[data-infinite-fallback]')?.removeAttribute('hidden');
    } finally {
      if (status) status.hidden = true;
      this.#loading = false;
      this.#controller = null;
    }
  }

  #finish() {
    this.#observer?.disconnect();
    delete this.dataset.nextUrl;
  }
}

if (!customElements.get('s-infinite-scroll')) {
  customElements.define('s-infinite-scroll', InfiniteScroll);
}
